import type { IntlShape } from 'react-intl';
import { BOOK_BANK_STATUS_TYPES, BOOK_BANK_STATUS_TYPE_ENUM } from 'enums';
import { getProjectCompanyCodeValues, normalizeProjectCompanyCodes } from '../product-company-codes';
import type { Book, BookProject, ProductReportRow, ProjectCompanyCode, ProjectCompanyCodeBank } from 'library/types';

export type ProductBankAssignment = {
  companyCode: string;
  bankId: number;
  status: BOOK_BANK_STATUS_TYPE_ENUM;
};

export type ProductBankSummary = {
  assignmentCount: number;
  bankLimitUsageCount: number;
  uniqueBankCount: number;
  assignedCompanyCount: number;
  statusCounts: Record<BOOK_BANK_STATUS_TYPE_ENUM, number>;
};

export type ProductBankLimitState = 'available' | 'reached' | 'exceeded' | 'invalid';

export type ProductBankConfiguration = {
  id: number;
  companyCodes?: readonly ProjectCompanyCode[] | null;
  bankCount: unknown;
  banks?: readonly ProjectCompanyCodeBank[] | null;
};

export type ProductBankValidationIssue =
  | { code: 'invalid-bank-limit' }
  | { code: 'bank-limit-exceeded'; bankLimitUsageCount: number; bankLimit: number }
  | { code: 'bank-company-code-mismatch'; companyCode: string };

export type BookProductBankValidationIssue<TProject extends ProductBankConfiguration = ProductBankConfiguration> = {
  project: TProject;
  issue: ProductBankValidationIssue;
};

const toArray = <T>(value: readonly T[] | null | undefined): readonly T[] => (Array.isArray(value) ? value : []);

export const createEmptyBankStatusCounts = (): Record<BOOK_BANK_STATUS_TYPE_ENUM, number> => ({
  [BOOK_BANK_STATUS_TYPE_ENUM.NONE]: 0,
  [BOOK_BANK_STATUS_TYPE_ENUM.CONTINUES]: 0,
  [BOOK_BANK_STATUS_TYPE_ENUM.COMPLETED]: 0,
  [BOOK_BANK_STATUS_TYPE_ENUM.PASSIVE]: 0,
});

const normalizeBankStatus = (status: unknown): BOOK_BANK_STATUS_TYPE_ENUM => {
  const normalizedStatus = Number(status) as BOOK_BANK_STATUS_TYPE_ENUM;

  return BOOK_BANK_STATUS_TYPES.includes(normalizedStatus) ? normalizedStatus : BOOK_BANK_STATUS_TYPE_ENUM.NONE;
};

export const countsTowardsBankLimit = (status: BOOK_BANK_STATUS_TYPE_ENUM): boolean =>
  status !== BOOK_BANK_STATUS_TYPE_ENUM.PASSIVE;

export const normalizeProductBankLimit = (bankCount: unknown): number | null => {
  const normalizedLimit = Number(bankCount);

  return Number.isFinite(normalizedLimit) && Number.isSafeInteger(normalizedLimit) && normalizedLimit >= 0
    ? normalizedLimit
    : null;
};

export const getProductBankLimitState = (bankCount: unknown, bankLimitUsageCount: number): ProductBankLimitState => {
  const normalizedLimit = normalizeProductBankLimit(bankCount);

  if (normalizedLimit === null) return 'invalid';
  if (bankLimitUsageCount > normalizedLimit) return 'exceeded';
  if (bankLimitUsageCount === normalizedLimit) return 'reached';

  return 'available';
};

export const flattenProductBankAssignments = (
  groups: readonly ProjectCompanyCodeBank[] | null | undefined
): ProductBankAssignment[] =>
  toArray(groups).flatMap((group) =>
    toArray(group.banks).flatMap((bank) => {
      const bankId = Number(bank.bankId);

      if (!Number.isFinite(bankId) || bankId <= 0) return [];

      return [
        {
          companyCode: String(group.companyCode ?? ''),
          bankId,
          status: normalizeBankStatus(bank.status),
        },
      ];
    })
  );

export const summarizeProductBanks = (
  groups: readonly ProjectCompanyCodeBank[] | null | undefined
): ProductBankSummary => {
  const assignments = flattenProductBankAssignments(groups);
  const statusCounts = createEmptyBankStatusCounts();
  let bankLimitUsageCount = 0;

  assignments.forEach(({ status }) => {
    statusCounts[status] += 1;
    if (countsTowardsBankLimit(status)) bankLimitUsageCount += 1;
  });

  return {
    assignmentCount: assignments.length,
    bankLimitUsageCount,
    uniqueBankCount: new Set(assignments.map(({ bankId }) => bankId)).size,
    assignedCompanyCount: new Set(assignments.map(({ companyCode }) => companyCode)).size,
    statusCounts,
  };
};

export const validateProductBankConfiguration = (
  project: ProductBankConfiguration
): ProductBankValidationIssue | null => {
  const bankLimit = normalizeProductBankLimit(project.bankCount);

  if (bankLimit === null) return { code: 'invalid-bank-limit' };

  const { bankLimitUsageCount } = summarizeProductBanks(project.banks);

  if (bankLimitUsageCount > bankLimit) {
    return { code: 'bank-limit-exceeded', bankLimitUsageCount, bankLimit };
  }

  const selectedCompanyCodes = new Set(getProjectCompanyCodeValues(project.companyCodes));
  const mismatchedGroup = toArray(project.banks).find(
    (group) => toArray(group.banks).length > 0 && !selectedCompanyCodes.has(String(group.companyCode ?? ''))
  );

  if (mismatchedGroup) {
    return { code: 'bank-company-code-mismatch', companyCode: String(mismatchedGroup.companyCode ?? '') };
  }

  return null;
};

export const findBookProductBankValidationIssue = <TProject extends ProductBankConfiguration>(
  projects: readonly TProject[] | null | undefined
): BookProductBankValidationIssue<TProject> | null => {
  for (const project of toArray(projects)) {
    const issue = validateProductBankConfiguration(project);

    if (issue) return { project, issue };
  }

  return null;
};

export const getProductBankValidationMessage = (
  issue: ProductBankValidationIssue,
  productTitle: string,
  intl: IntlShape
): string => {
  switch (issue.code) {
    case 'invalid-bank-limit':
      return intl.formatMessage({
        id: 'project.validation.bankLimitInvalid',
        defaultMessage: 'Banka limiti 0 veya daha büyük bir tam sayı olmalıdır!',
      });
    case 'bank-limit-exceeded':
      return intl.formatMessage(
        {
          id: 'project.validation.productBankLimitExceeded',
          defaultMessage: '{product} ürününde pasif olmayan banka sayısı ({current}), banka limitini ({max}) aşamaz!',
        },
        { product: productTitle, current: issue.bankLimitUsageCount, max: issue.bankLimit }
      );
    case 'bank-company-code-mismatch':
      return intl.formatMessage(
        {
          id: 'project.validation.bankCompanyCodeMismatch',
          defaultMessage:
            '{product} ürünündeki {companyCode} banka grubu, ürünün seçili şirket kodları arasında yer almalıdır!',
        },
        { product: productTitle, companyCode: issue.companyCode }
      );
  }
};

export const summarizeBookProjectBanks = (projects: readonly BookProject[] | null | undefined): ProductBankSummary => {
  const allGroups = toArray(projects).flatMap((project) => toArray(project.banks));

  return summarizeProductBanks(allGroups);
};

export const hasBankAssignment = (
  projects: readonly BookProject[] | null | undefined,
  bankId: number | string
): boolean => {
  const normalizedBankId = Number(bankId);

  if (!Number.isFinite(normalizedBankId)) return false;

  return toArray(projects).some((project) =>
    flattenProductBankAssignments(project.banks).some((assignment) => assignment.bankId === normalizedBankId)
  );
};

export const createBankTitleMap = (banks: readonly { id: number; title: string }[]): Map<number, string> =>
  new Map(banks.map((bank) => [bank.id, bank.title]));

export const buildProductReportRows = (books: readonly Book[]): ProductReportRow[] =>
  books.flatMap((book) =>
    toArray(book.projects).map((project, projectIndex) => {
      const bankSummary = summarizeProductBanks(project.banks);

      return {
        id: `${book.id}-${project.id}-${projectIndex}`,
        bookId: book.id,
        projectName: book.companyName ?? '',
        bookProject: project,
        projectId: project.id,
        environmentTypeId: project.environmentTypeId,
        companyCodes: normalizeProjectCompanyCodes(project.companyCodes),
        companies: Array.isArray(book.companies) ? book.companies : [],
        bankCount: Number(project.bankCount) || 0,
        banks: Array.isArray(project.banks) ? project.banks : [],
        bankAssignmentCount: bankSummary.assignmentCount,
        bankLimitUsageCount: bankSummary.bankLimitUsageCount,
        uniqueBankCount: bankSummary.uniqueBankCount,
        bankStatusCounts: bankSummary.statusCounts,
      };
    })
  );
